import { Stack } from '@mui/joy';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IInputProps, SearchList, SearchListType } from 'core/components';
import { useGetPartsSearch } from 'parts/hooks';
import { IPart, IPartsSearchRequestPayload } from 'parts/types';
import { Direction } from 'theme/enums';

import {
  InventoryFilterChips,
  IPartSearchFiltersState,
  PartSearchFilters,
  usePartSearchFilters,
} from './filters';
import { PartCard } from './PartCard';
import { inputStyle } from './style';
import { IPartDetailsPayload, PartDetails, PartDetailsTab } from '../PartDetails';

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  title?: string;
  defaultQuery?: string;
  onSearchQuery?: (query?: string) => void;
  partSearchPayload?: IPartsSearchRequestPayload;
  getPartCardButtons?: (part: IPart) => JSX.Element[];
  isAlternateVersionEnabled?: boolean;
  level?: number;
  type?: SearchListType;
  partDetailsPayload?: IPartDetailsPayload;
  filtersState?: IPartSearchFiltersState;
}

export const SearchParts = (props: IProps) => {
  if (props.filtersState) {
    return <SearchPartsBody {...props} filtersState={props.filtersState} />;
  }

  return <SearchPartsWithInternalFilters {...props} />;
};

const SearchPartsWithInternalFilters = (props: IProps) => {
  const internalFiltersState = usePartSearchFilters();
  const { resetFilters } = internalFiltersState;
  const wasOpenRef = useRef(false);
  const { isOpen, type = SearchListType.COMPACT } = props;

  useEffect(() => {
    if (type !== SearchListType.COMPACT) {
      return;
    }

    // Reset only on the open→close transition; skip initial mount and closed-to-closed re-renders.
    if (wasOpenRef.current && !isOpen) {
      resetFilters();
    }

    wasOpenRef.current = !!isOpen;
  }, [isOpen, type, resetFilters]);

  return <SearchPartsBody {...props} filtersState={internalFiltersState} />;
};

interface IBodyProps extends IProps {
  filtersState: IPartSearchFiltersState;
}

const SearchPartsBody = ({
  isOpen,
  onClose,
  title,
  defaultQuery = '',
  onSearchQuery,
  partSearchPayload = {},
  getPartCardButtons,
  isAlternateVersionEnabled,
  level = 0,
  type = SearchListType.COMPACT,
  partDetailsPayload,
  filtersState,
}: IBodyProps) => {
  const { filters, criteria, isActive, resetFilters, onChangeInventoryFilter } = filtersState;

  const { t } = useTranslation();

  const searchInputProps: IInputProps = useMemo(
    () => ({
      placeholder: t('labelProductSearch'),
      slotProps: { input: { sx: inputStyle } },
    }),
    [t]
  );

  const mergedSearchPayload = useMemo(() => {
    if (!isAlternateVersionEnabled || !criteria) {
      return partSearchPayload;
    }

    return {
      ...partSearchPayload,
      criteria: { ...partSearchPayload.criteria, ...criteria },
    };
  }, [partSearchPayload, criteria, isAlternateVersionEnabled]);

  const inlineFilters = useMemo(
    () => (
      <PartSearchFilters
        filters={filters}
        isActive={isActive}
        onResetFilters={resetFilters}
        onChangeInventoryFilter={onChangeInventoryFilter}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />
    ),
    [filters, isActive, resetFilters, onChangeInventoryFilter, isAlternateVersionEnabled]
  );

  const appliedFilters = useMemo(() => {
    if (!isAlternateVersionEnabled) {
      return null;
    }

    return (
      <Stack direction={Direction.ROW} spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <InventoryFilterChips value={filters.inventory} onChange={onChangeInventoryFilter} />
      </Stack>
    );
  }, [isAlternateVersionEnabled, filters.inventory, onChangeInventoryFilter]);

  const getItemKey = useCallback(
    (part: IPart) => [part.id, part.manufacturerId, part.vendorNumber].filter(Boolean).join('-'),
    []
  );

  const renderItem = (part: IPart, onSelectItemClick: (part: IPart) => void) => (
    <PartCard
      part={part}
      onClick={onSelectItemClick}
      getPartCardButtons={getPartCardButtons}
      isAlternateVersionEnabled={isAlternateVersionEnabled}
    />
  );

  const renderDetails = (
    isDetailsOpen: boolean,
    onCloseDetails: () => void,
    selectedItem: IPart | null
  ) => {
    return (
      <PartDetails
        isOpen={isDetailsOpen}
        onClose={onCloseDetails}
        part={selectedItem}
        partDetailsPayload={partDetailsPayload}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        {...(type === SearchListType.FULL && {
          defaultTab: PartDetailsTab.INVENTORY,
          slidingPanelProps: {
            interactive: true,
            autoWidth: true,
          },
        })}
      />
    );
  };

  return (
    <SearchList<IPart>
      isOpen={isOpen}
      onClose={onClose}
      title={title || t('titleFindAPartNumber')}
      defaultQuery={defaultQuery}
      onSearchQuery={onSearchQuery}
      searchPayload={mergedSearchPayload}
      useGetSearchList={useGetPartsSearch}
      getItemKey={getItemKey}
      renderItem={renderItem}
      renderDetails={renderDetails}
      level={level}
      type={type}
      searchInputProps={searchInputProps}
      inlineFilters={inlineFilters}
      appliedFilters={appliedFilters}
    />
  );
};
